// The question thread, pinned under the transcript at the bottom right (docs/design/CALL-POP-KRISP-BAR.md lane 3; lane 3
// owns this file). One fixed 9rem box: the thread on top (fixed-height rows, newest at the bottom, scrolling inside
// itself), three fixed chips, then the one text field. Enter sends, Escape clears, Ctrl/Cmd+Shift+K focuses the field
// from anywhere in the window. Every row has the same height whatever it holds: the question line (question, source
// tags, copy, thumbs) and three reserved answer lines, where "Thinking" sits until the answer lands in the same place.
// Nothing outside this box moves, and nothing inside it moves when a row changes state. CallWindowStyles concatenates
// QUESTION_THREAD_CSS. Icons through ui/Icon.tsx only (inline SVG).
import { type FormEvent, type KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { ASK_SOURCES, type AskSource, type CoachAskAnswer } from '@bcio-voice/contracts';
import type { CoachQaEntry } from '../../call/coach/coach.ts';
import { isRepOnlyQuickAction } from '../../call/coach/coach.ts';
import { ASK_MAX_LENGTH, cleanQuestion, escapeClears } from '../transcript/ask.ts';
import { Icon } from '../../ui/Icon.tsx';
import { TipFeedback } from './TipFeedback.tsx';

export interface QuestionThreadProps {
  callId: string | null;
  /** Stable per window: element ids. */
  idBase: string;
  /** Newest first, as the coach's Q&A reducer keeps them. The thread shows them newest at the bottom. */
  entries: readonly CoachQaEntry[];
  onAsk(question: string): void;
  /** Asking is off (off hours, on hold, ended, Coach not available): the field is read-only and drawn off. */
  disabled: boolean;
  disabledReason: string | null;
  /** A question is in flight: the chips and Ask wait for it, and the field still takes typing, so the next question can
   * be typed ahead and sent once the answer lands (final verification, round 2: read-only on every ask dimmed the field
   * and dropped what the rep typed). */
  busy?: boolean;
  /** The viewer's thumbs per answer (entry id), and the vote handler; both optional until the window wires them. */
  votes?: Readonly<Record<string, 'up' | 'down'>>;
  onFeedback?(entryId: string, vote: 'up' | 'down'): void;
}

/** The three fixed chips above the field (spec lane 3), sent as the question text. */
export const QUESTION_CHIPS = ['What did they object to?', 'What do they already pay for?', 'What did we agree to?'] as const;
export const QUESTION_PLACEHOLDER = 'Ask about this call or the account';
/** The loading row's status, muted, in the row's reserved first answer line. */
export const QUESTION_THINKING = 'Thinking';
/** The empty thread is an empty reserved row: no explanation (section 4b), and never the reason asking is off, which the
 * field's placeholder already says once. */
export const QUESTION_EMPTY = '';
/** The tag shown for each source of an answer. */
export const SOURCE_LABELS: Readonly<Record<AskSource, string>> = { call: 'call', crm: 'CRM', general: 'general' };
const GENERAL_LABEL = 'General knowledge:';
const FLAT = 'Not in the CRM or this call.';

/** Ctrl+Shift+K, or Cmd+Shift+K on a Mac; never with Alt. By code, so a keyboard layout or Caps Lock cannot change it. */
export function isQuestionShortcut(e: Pick<globalThis.KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>): boolean {
  return (e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.code === 'KeyK';
}

/**
 * Where an answer came from, in the contract's order. The server's `sources` when the answer carries them; otherwise
 * (a reply read without the field) what was sent to the coach: the call when transcript lines were used, the CRM when
 * the card was, and general knowledge when the text is labelled. A no_answer has none.
 */
export function answerSources(answer: CoachAskAnswer): AskSource[] {
  if (answer.state !== 'answered') return [];
  if (Array.isArray(answer.sources)) return ASK_SOURCES.filter(s => answer.sources!.includes(s));
  const text = `${answer.suggested_wording ?? ''} ${answer.suggestion}`;
  const neither = answer.suggestion.startsWith(FLAT);
  const out = new Set<AskSource>();
  if (!neither && answer.segments_used > 0) out.add('call');
  if (!neither && answer.crm_card_used) out.add('crm');
  if (text.includes(GENERAL_LABEL)) out.add('general');
  return ASK_SOURCES.filter(s => out.has(s));
}

/** The answer as the row shows it: the sayable part first (never for a rep-only quick action), then the rest. */
export function answerParts(question: string, answer: CoachAskAnswer): { say: string | null; text: string } {
  const say = answer.state === 'answered' && !isRepOnlyQuickAction(question) ? answer.suggested_wording : null;
  return { say: say?.trim() ? say.trim() : null, text: answer.suggestion };
}

/** What the copy button puts on the clipboard: the answer as shown, sayable part first. */
export function answerCopyText(question: string, answer: CoachAskAnswer): string {
  const { say, text } = answerParts(question, answer);
  return say ? `${say} ${text}` : text;
}

/** 9rem in all: the thread (rows of one question line and three answer lines), the chip row and the field row. In a
 * short call window (under 720 px, as CallWindowView's compact rules) 123 px: answer rows of two lines. overflow: clip, so
 * focusing the field never scrolls the box. */
export const QUESTION_THREAD_CSS = `
.cw-qt { --cw-qt-line: var(--leading-meta); --cw-qt-row: calc(4 * var(--cw-qt-line)); height: 9rem; box-sizing: border-box; min-height: 0; min-width: 0; overflow: clip; display: grid; grid-template-rows: minmax(0, 1fr) 24px 32px; row-gap: var(--space-1); border-top: 1px solid var(--rule); padding-top: var(--space-1); }
.cw-qt, .cw-qt * { animation: none !important; transition: none !important; }
.cw-qt p { margin: 0; }
.cw-qt-thread { min-height: 0; overflow-y: auto; scrollbar-gutter: stable; overscroll-behavior: contain; margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: var(--space-1); }
/* Newest at the bottom: the first row takes the free space above it, and once the rows overflow the auto margin is 0
   and every row stays reachable by scrolling. The newest row is always in the same place. */
.cw-qt-thread > :first-child { margin-top: auto; }
.cw-qt-empty { flex: none; height: var(--cw-qt-line); font-size: var(--text-meta); line-height: var(--cw-qt-line); color: var(--ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cw-qt-row { flex: none; height: var(--cw-qt-row); min-width: 0; display: grid; grid-template-rows: var(--cw-qt-line) calc(3 * var(--cw-qt-line)); font-size: var(--text-meta); line-height: var(--cw-qt-line); }
/* The tag column fits all three tags (call, CRM, general) and is reserved even while a row has none. */
.cw-qt-head { min-width: 0; display: grid; grid-template-columns: minmax(0, 1fr) 8rem 18px calc(2 * 16px + var(--space-2)); column-gap: var(--space-1); align-items: center; }
.cw-qt-question { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: var(--wght-strong); color: var(--ink-2); }
.cw-qt-tags { min-width: 0; height: 16px; display: flex; justify-content: flex-end; gap: var(--space-1); overflow: hidden; }
.cw-qt-tag { flex: none; box-sizing: border-box; height: 16px; padding: 0 var(--space-1); border: 1px solid var(--rule-strong); border-radius: var(--radius-control); font-size: var(--text-min); line-height: 14px; color: var(--ink-3); font-weight: var(--wght-medium); letter-spacing: var(--track-small); }
.cw-qt-copy { box-sizing: border-box; width: 18px; height: 18px; padding: 0; margin: 0; border: 0; border-radius: var(--radius-control); background: transparent; color: var(--ink-3); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; }
.cw-qt-copy:hover { color: var(--ink); background: var(--sunken); }
.cw-qt-copy:disabled { visibility: hidden; }
.cw-qt-fb { min-width: 0; height: 16px; display: flex; justify-content: flex-end; overflow: hidden; }
.cw-qt-a { min-width: 0; height: calc(3 * var(--cw-qt-line)); overflow: hidden; overflow-wrap: anywhere; color: var(--ink-2); display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.cw-qt-say { color: var(--brass); }
.cw-qt-a.is-muted { color: var(--ink-3); }
.cw-qt-a.is-warn { color: var(--warn); }
.cw-qt-chips { min-width: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--space-2); }
.cw-qt-chip.btn { min-width: 0; min-height: 0; height: 24px; padding: 0 var(--space-2); font-size: var(--text-min); overflow: hidden; justify-content: flex-start; }
.cw-qt-chip > span { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cw-qt-form { margin: 0; min-width: 0; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: var(--space-2); height: 32px; }
.cw-qt-form input { box-sizing: border-box; min-width: 0; height: 32px; margin: 0; padding: 0 var(--space-3); border: 1px solid var(--control-border); border-radius: var(--radius-control); background: var(--surface); color: var(--ink); font: inherit; font-size: var(--text-body); }
.cw-qt-form input::placeholder { color: var(--ink-3); }
/* Asking is off (off hours, on hold, ended, Coach not available): the field takes no typing (readOnly) and is drawn as
   off, as the chips and Ask beside it are, so a question is never typed and then silently ignored (review round 3). */
.cw-qt-form input[aria-disabled='true'] { color: var(--ink-3); cursor: not-allowed; opacity: 0.55; }
.cw-qt-form .btn { height: 32px; min-height: 0; }
@media (max-height: 719px) {
  .cw .cw-qt { --cw-qt-row: calc(3 * var(--cw-qt-line)); height: 123px; }
  .cw .cw-qt-row { grid-template-rows: var(--cw-qt-line) calc(2 * var(--cw-qt-line)); }
  .cw .cw-qt-a { height: calc(2 * var(--cw-qt-line)); -webkit-line-clamp: 2; }
}
`;

function Row({ entry, copied, onCopy, vote, onVote }: {
  entry: CoachQaEntry;
  copied: boolean;
  onCopy: (entry: CoachQaEntry & { status: 'answered' }) => void;
  vote: 'up' | 'down' | null;
  onVote: (vote: 'up' | 'down') => void;
}) {
  const answered = entry.status === 'answered' ? entry : null;
  const sources = answered ? answerSources(answered.answer) : [];
  const parts = answered ? answerParts(entry.question, answered.answer) : null;
  const found = answered?.answer.state === 'answered';
  const full = parts ? (parts.say ? `Say: ${parts.say} ${parts.text}` : parts.text) : undefined;
  return (
    <li className="cw-qt-row" data-qt="row" data-status={entry.status === 'answered' ? entry.answer.state : entry.status} aria-busy={entry.status === 'loading' || undefined}>
      <div className="cw-qt-head">
        <p className="cw-qt-question" title={entry.question}><span className="visually-hidden">Question: </span>{entry.question}</p>
        <span className="cw-qt-tags" data-qt="tags">
          {sources.length ? <span className="visually-hidden">Sources: </span> : null}
          {sources.map(s => <span key={s} className="cw-qt-tag" data-source={s}>{SOURCE_LABELS[s]}</span>)}
        </span>
        <button type="button" className="cw-qt-copy" data-qt="copy" disabled={!found} aria-label={copied ? 'Copied' : 'Copy the answer'} title={copied ? 'Copied' : 'Copy the answer'}
          onClick={() => { if (answered && found) onCopy(answered); }}>
          <Icon icon={copied ? Check : Copy} size="1em" />
        </button>
        <span className="cw-qt-fb" data-qt="feedback">
          <TipFeedback target="answer" targetId={found ? entry.id : null} voted={found ? vote : null} onVote={onVote} />
        </span>
      </div>
      {entry.status === 'loading' ? (
        <p className="cw-qt-a is-muted" role="status">{QUESTION_THINKING}</p>
      ) : entry.status === 'error' ? (
        <p className="cw-qt-a is-warn" title={entry.error.detail || undefined}>{entry.error.title}</p>
      ) : !found ? (
        <p className="cw-qt-a is-muted">{entry.answer.suggestion}</p>
      ) : (
        <p className="cw-qt-a" title={full}>
          {parts!.say ? <><span className="cw-qt-say"><strong>Say:</strong> {parts!.say}</span>{' '}</> : null}
          <span className="visually-hidden">{parts!.say ? 'For you: ' : 'Answer: '}</span>{parts!.text}
        </p>
      )}
    </li>
  );
}

export function QuestionThread(props: QuestionThreadProps) {
  const { idBase, entries, disabled, disabledReason } = props;
  const blocked = disabled || props.busy === true;
  const inputId = `cw-qt-${idBase}`;
  const [draft, setDraft] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const atBottom = useRef(true);
  const rows = [...entries].reverse();

  // A new call starts with an empty field.
  useEffect(() => { setDraft(''); setCopiedId(null); }, [props.callId]);
  // Ctrl/Cmd+Shift+K from anywhere in the window (lib/shortcuts.ts lists it).
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!isQuestionShortcut(event)) return;
      event.preventDefault();
      // Never scroll a clipped container to reach the field (a short window): nothing on screen moves for a shortcut.
      input.current?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (copiedId === null) return;
    const t = setTimeout(() => setCopiedId(null), 2000);
    return () => clearTimeout(t);
  }, [copiedId]);
  // Rows never change height, so only a new question can grow the thread: keep the newest in view while the reader is
  // at the bottom, and leave the thread alone once they scroll up.
  const signature = rows.map(e => e.id).join('|');
  useLayoutEffect(() => {
    const el = list.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [signature]);
  // A question the rep asks is the row they want to see, even after scrolling up to reread an older answer: the thread
  // follows again from here, so its Thinking line and then its answer land in view (review round 3: they appended below
  // the visible rows, and the rep took the cleared field for Ask ignoring them). Only the thread's own rows scroll.
  const follow = () => {
    atBottom.current = true;
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const ask = (question: string) => {
    if (blocked || !cleanQuestion(question)) return;
    follow();
    props.onAsk(question);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (blocked || !cleanQuestion(draft)) return;
    follow();
    props.onAsk(draft);
    setDraft('');
  };
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (escapeClears(e.key, draft)) { e.preventDefault(); setDraft(''); }
  };
  // "Copied" only once the clipboard took it; without a clipboard (an insecure page) the button just does nothing.
  const copy = (entry: CoachQaEntry & { status: 'answered' }) => {
    const text = answerCopyText(entry.question, entry.answer);
    try { void navigator.clipboard?.writeText(text).then(() => setCopiedId(entry.id), () => undefined); } catch { /* no clipboard */ }
  };
  // Why asking is off, once, in the field's own placeholder: text inside a reserved box.
  const reason = disabled && disabledReason ? disabledReason : null;
  return (
    <section className="cw-qt" data-slot="question-thread" aria-label="Questions about this call">
      <ol ref={list} className="cw-qt-thread" data-qt="thread" role="log" aria-live="polite" aria-label="Answers, newest at the bottom" tabIndex={0}
        onScroll={e => { const el = e.currentTarget; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 8; }}>
        {rows.length ? rows.map(entry => (
          <Row key={entry.id} entry={entry} copied={copiedId === entry.id} onCopy={copy}
            vote={props.votes?.[entry.id] ?? null} onVote={vote => props.onFeedback?.(entry.id, vote)} />
        )) : <li className="cw-qt-empty" data-qt="empty" aria-hidden="true">{QUESTION_EMPTY}</li>}
      </ol>
      <div className="cw-qt-chips" data-qt="chips" role="group" aria-label="Quick questions">
        {QUESTION_CHIPS.map(q => (
          <button key={q} type="button" className="btn btn-small cw-qt-chip" data-qt="chip" disabled={blocked} title={q} onClick={() => ask(q)}><span>{q}</span></button>
        ))}
      </div>
      <form className="cw-qt-form" data-qt="form" onSubmit={submit} noValidate>
        <label className="visually-hidden" htmlFor={inputId}>Ask about this call or the account</label>
        <input ref={input} id={inputId} data-qt="input" type="text" maxLength={ASK_MAX_LENGTH} autoComplete="off" enterKeyHint="send"
          placeholder={reason ?? QUESTION_PLACEHOLDER} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={keys}
          aria-disabled={disabled || undefined} readOnly={disabled} title={reason ?? undefined} aria-keyshortcuts="Control+Shift+K Meta+Shift+K" />
        <button type="submit" className="btn btn-small" data-qt="send" disabled={blocked || !cleanQuestion(draft)}>Ask</button>
      </form>
    </section>
  );
}
